// src/pages/StudyPlanPage.jsx - REDESIGNED TO MATCH GRAMMAR HUB
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';
// Add these imports
import { ModuleSection } from './ModuleSection.jsx';
import { LessonRow } from './LessonRow.jsx';
import {
  ChevronLeft, ChevronRight, Target, BookOpen, Zap, GraduationCap,
  Clock, BarChart3, TrendingUp, Timer, CheckCircle, Lock, Crown, ArrowRight, X, Home, ClipboardList, Play
} from 'lucide-react';
import GrammarBottomNav from '../grammar/GrammarBottomNav.jsx';
import { auth } from '../../firebase';
import subscriptionService from '../../services/subscriptionService';
import adminService from '../../services/adminService';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

// --- Custom Hook Imports ---
import { useUserStats } from '../../hooks/useUserStats.js';
import { useGamification } from '../../hooks/useGamification.js';

// --- Helper Components & Functions ---
const LoadingSpinner = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <LogamiLogoSpinner size="xl" className="mx-auto mb-4" decorative={false} alt="Logami loading" />
        <p className="text-black/60 font-medium">{t('studyPlan.loading')}</p>
      </div>
    </div>
  );
};

const fetchStudyPlan = async (provider, level) => {
  const examKey = `${provider.toLowerCase()}-${level.toLowerCase()}`;
  try {
    const response = await fetch(`/data/lessons/study-plans/${examKey}-study-plan.json`);
    if (!response.ok) throw new Error('Network response was not ok');
    return await response.json();
  } catch (error) {
    console.error("Failed to fetch study plan:", error);
    return null;
  }
};

const StudyPlanPage = ({ examProvider = 'TELC', examLevel = 'B2', seoH1, examSlug }) => {
    const location = useLocation();
    const openLessonId = location.state?.openLessonId ?? null;
    const [studyPlan, setStudyPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentWeek, setCurrentWeek] = useState(1);
    const navigate = useLocalizedNavigate();
    const [hoveredLessonId, setHoveredLessonId] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
    const [videoModal, setVideoModal] = useState(null); // { videoId, title }
    // Real hook integration
    const {userStats, updateUserProgress} = useUserStats();
    const {awardXp} = useGamification();
    const { t } = useTranslation();
    const user = auth.currentUser;

    // Load subscription status
    useEffect(() => {
        const loadSubscriptionStatus = async () => {
            if (user) {
                try {
                    const status = await subscriptionService.getSubscriptionStatus(user.uid);
                    setSubscriptionStatus(status);
                } catch (error) {
                    console.error('Error loading subscription status:', error);
                    setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
                }
            } else {
                setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
            }
        };
        loadSubscriptionStatus();
    }, [user]);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            const plan = await fetchStudyPlan(examProvider, examLevel);
            // Show all modules (no filter - modules 2-9 will be locked for free users)
            setStudyPlan(plan);
            setIsLoading(false);
        };
        loadData();
    }, [examProvider, examLevel]);

    // When opened from lesson/practice with openLessonId, scroll that lesson into view
    useEffect(() => {
        if (!studyPlan || !openLessonId) return;
        const el = document.querySelector(`[data-lesson-id="${openLessonId}"]`);
        if (el) {
            const t = setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
            return () => clearTimeout(t);
        }
    }, [studyPlan, openLessonId]);

    // Check if module is locked (only module 1 is free, admins have access to all)
    const isModuleLocked = (moduleId) => {
        if (moduleId === 'module_01_intro') return false; // Module 1 is always available
        if (adminService.isAdmin()) return false; // Admins have access to all modules
        return subscriptionStatus?.isFree || false; // Other modules require premium
    };

    // Free preview rule: everyone can open Reading/Lesen Part 1.
    const isLesenPartOneLesson = (lesson, module, lessonIndex) => {
      const moduleId = String(module?.moduleId || '').toLowerCase();
      const moduleTitle = String(module?.title || '').toLowerCase();
      const lessonId = String(lesson?.lessonId || '').toLowerCase();
      const lessonTitle = String(lesson?.title || '').toLowerCase();

      const isReadingModule =
        moduleId.includes('lesen') ||
        moduleId.includes('reading') ||
        moduleTitle.includes('lese') ||
        moduleTitle.includes('reading');

      if (!isReadingModule) return false;

      const partOnePattern = /(teil\s*1|part\s*1)/i;
      const idPartOnePattern = /(part[_-]?1|teil[_-]?1|t1)/;
      const isReadingLesson =
        /(lv[_-]?|lesen[_-]?|reading[_-]?)/.test(lessonId) ||
        /(lese|reading)/i.test(lessonTitle);

      return isReadingLesson && (idPartOnePattern.test(lessonId) || partOnePattern.test(lessonTitle) || lessonIndex === 0);
    };

    const isLessonFreePreview = (lesson, module, lessonIndex) => {
      if (module?.moduleId === 'module_01_intro') return true;
      return isLesenPartOneLesson(lesson, module, lessonIndex);
    };

    // Dynamic progress calculation
    const {lessonStatusMap, dashboardProgress} = useMemo(() => {
        if (!studyPlan) return {lessonStatusMap: {}, dashboardProgress: {}};

        const completedLessons = new Set(userStats?.completedLessons || []);
        const statusMap = {};
        let completedCount = 0;
        let totalDuration = 0;
        let completedDuration = 0;

        const allLessons = studyPlan.modules.flatMap(m => m.lessons);

        allLessons.forEach(lesson => {
            totalDuration += lesson.duration;

            if (lesson.type === 'video' && !lesson.videoId) {
                statusMap[lesson.lessonId] = 'coming_soon';
                return;
            }

            if (completedLessons.has(lesson.lessonId)) {
                statusMap[lesson.lessonId] = 'completed';
                completedCount++;
                completedDuration += lesson.duration;
            } else {
                statusMap[lesson.lessonId] = 'available';
            }
        });

        return {
            lessonStatusMap: statusMap,
            dashboardProgress: {
                completed: completedCount,
                total: allLessons.filter(l => l.type !== 'video').length,
                estimatedTime: Math.round((totalDuration - completedDuration) / 60),
            }
        };
    }, [studyPlan, userStats]);

    // Lesson completion handler
    const handleLessonComplete = async (lesson) => {
        await awardXp(lesson.xp || 50, 'lesson_completion');
        await updateUserProgress({
            completedLessonId: lesson.lessonId,
            timeSpent: lesson.duration
        });
    };

    // When on exam-scoped lessons (e.g. /exams/telc-b1/lessons), keep all links under that exam
    const navBase = examSlug ? `/exams/${examSlug}` : '';

    // Navigation logic
    const handleStartLesson = (lesson, module, lessonIndex) => {
      const moduleId = module?.moduleId;
        // Prevent action for coming soon lessons
        if (lesson.type === 'video' && !lesson.videoId) return;
        if (lesson.type === 'video' && lesson.videoId) {
            setVideoModal({ videoId: lesson.videoId, title: lesson.title });
            return;
        }
        // Check if module is locked
      if (isModuleLocked(moduleId) && !isLessonFreePreview(lesson, module, lessonIndex)) {
            setShowPremiumOverlay(true);
            return;
        }

        if (lesson.lessonId.startsWith('grammar') || lesson.dataUrl?.includes('grammar')) {
            if (lesson.title?.toLowerCase().includes('practice') ||
                lesson.title?.toLowerCase().includes('übung') ||
                lesson.title?.toLowerCase().includes('training')) {
                navigate(navBase ? `${navBase}/practice/grammar` : '/practice/grammar');
            } else {
                navigate(navBase ? `${navBase}/practice/grammar` : '/study/grammar');
            }
            return;
        }

        const strategyPageTypes = ['theory_practice', 'speaking_practice', 'writing_practice', 'strategy'];
        if (lesson.dataUrl || strategyPageTypes.includes(lesson.type)) {
            navigate(navBase ? `${navBase}/lesson-strategy` : '/lesson-strategy', {
                state: {
                    dataUrl: lesson.dataUrl,
                    currentLessonId: lesson.lessonId,
                    studyPlan: studyPlan,
                    lessonStatusMap: lessonStatusMap,
                }
            });
            return;
        }

        if (lesson.lessonId === 'exam_structure') {
            navigate(`/exam-structure/${examProvider.toLowerCase()}/${examLevel.toLowerCase()}`);
            return;
        }

        switch (lesson.type) {
            case 'lesen_practice':
                navigate(navBase ? `${navBase}/practice/reading` : '/practice/reading');
                return;
            case 'horen_practice':
                navigate(navBase ? `${navBase}/practice/listening` : '/practice/listening');
                return;
            case 'schreiben_practice':
                navigate(navBase ? `${navBase}/practice/writing` : '/practice/writing');
                return;
            case 'vocabulary_trainer_link':
                navigate(navBase ? `${navBase}/practice/vocabulary` : '/practice/vocabulary');
                break;
            case 'redemittel_trainer_link':
                navigate(navBase ? `${navBase}/practice/redemittel` : '/practice/redemittel');
                break;
            case 'practice':
                if (lesson.lessonId.startsWith('grammar')) {
                    if (lesson.title?.toLowerCase().includes('practice') ||
                        lesson.title?.toLowerCase().includes('übung') ||
                        lesson.title?.toLowerCase().includes('training')) {
                        navigate(navBase ? `${navBase}/practice/grammar` : '/practice/grammar');
                    } else {
                        navigate(navBase ? `${navBase}/practice/grammar` : '/study/grammar');
                    }
                    break;
                }
                if (lesson.lessonId.startsWith('redemittel')) {
                    navigate(navBase ? `${navBase}/practice/redemittel` : '/practice/redemittel');
                    break;
                }
                const grammarModule = studyPlan.modules.find(m => m.lessons.some(l => l.lessonId === lesson.lessonId));
                const grammarSectionId = grammarModule ? grammarModule.moduleId.split('_')[2] : 'reading';
                navigate(navBase ? `${navBase}/practice/exam-section?sectionId=${grammarSectionId}` : `/practice/exam-section?sectionId=${grammarSectionId}`);
                break;
            case 'video':
            case 'theory':
                navigate(navBase ? `${navBase}/lesson-player/${lesson.lessonId}` : `/lesson-player/${lesson.lessonId}`);
                break;
            case 'exam_simulator_link':
                navigate(navBase ? `${navBase}/simulator` : '/exam/simulator');
                break;
            default:
                console.error(`Unknown lesson type: ${lesson.type}`);
                navigate(navBase || '/dashboard');
                break;
        }
    };

    if (isLoading) return <LoadingSpinner/>;
    if (!studyPlan) return <div className="min-h-screen bg-white flex items-center justify-center"><p
        className="text-black/60">Lernplan konnte nicht geladen werden.</p></div>;


    // Task icons
    const taskIcons = {
        vocabulary: <Target className="w-6 h-6"/>,
        grammar: <BookOpen className="w-6 h-6"/>,
        reading: <BookOpen className="w-6 h-6"/>,
        listening: <Zap className="w-6 h-6"/>,
        writing: <Zap className="w-6 h-6"/>,
        speaking: <Zap className="w-6 h-6"/>,
        lessons: <GraduationCap className="w-6 h-6"/>,
        lesen_practice: <BookOpen className="w-6 h-6"/>,
        horen_practice: <Zap className="w-6 h-6"/>,
        schreiben_practice: <Zap className="w-6 h-6"/>,
    };


    return (
        <div className="min-h-screen bg-white relative overflow-hidden">
            {/* Premium Overlay - Shows for free users when clicking locked lessons */}
            {showPremiumOverlay && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl border-2 border-black max-w-md w-full p-8 relative">
                        <button
                          onClick={() => setShowPremiumOverlay(false)}
                            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5 text-gray-600" />
                        </button>
                        <div className="text-center space-y-6">
                            <div className="w-20 h-20 bg-black/10 rounded-full flex items-center justify-center mx-auto">
                                <Lock className="w-10 h-10 text-black" />
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-2xl sm:text-3xl font-bold text-black">
                                    {t('premium.required.title')}
                                </h2>
                                <p className="text-lg text-black/60">
                                    {t('premium.required.descriptionLessons')}
                                </p>
                            </div>
                            <div className="space-y-3 pt-4">
                                <button
                                    onClick={() => navigate('/pricing')}
                                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    <Crown className="w-5 h-5" />
                                    {t('premium.required.upgrade')}
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => setShowPremiumOverlay(false)}
                                    className="w-full px-6 py-3 text-black/60 hover:text-black transition-colors duration-300"
                                >
                                  {t('premium.required.backLessons', { defaultValue: 'Back to Lessons' })}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Video Modal, in-app YouTube player */}
            {videoModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={() => setVideoModal(null)}>
                    <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setVideoModal(null)}
                            className="absolute -top-12 right-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-6 h-6 text-white" />
                        </button>
                        <div className="rounded-xl sm:rounded-2xl overflow-hidden border-2 border-white/20" style={{ position: 'relative', paddingTop: '56.25%' }}>
                            <iframe
                                src={`https://www.youtube-nocookie.com/embed/${videoModal.videoId}?rel=0&modestbranding=1&autoplay=1`}
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                                title={videoModal.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                        <p className="text-white text-center mt-4 font-space font-bold text-base sm:text-lg">{videoModal.title}</p>
                    </div>
                </div>
            )}
            {/* Header, compact, mobile-optimized typography & edge padding */}
            <div className="relative z-10 bg-white border-b-[3px] border-black/10 pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] sm:px-4 md:px-6 py-4 sm:py-5">
                <div className="max-w-7xl mx-auto">
                    <h1 className="font-space text-xl sm:text-2xl md:text-3xl font-black text-black mb-1 sm:mb-1.5 tracking-tight leading-tight">
                        {seoH1 || t(`studyPlan.title.${examProvider.toLowerCase()}.${examLevel.toLowerCase()}`, { defaultValue: studyPlan?.title })}
                    </h1>
                    <p className="font-sans text-sm sm:text-base text-black/60 leading-snug max-w-2xl">
                        {t(`studyPlan.description.${examProvider.toLowerCase()}.${examLevel.toLowerCase()}`, { defaultValue: studyPlan.description })}
                    </p>
                </div>
            </div>

            {/* Main Content, use edges on mobile with consistent gutters; pb for bottom nav */}
            <div className="relative z-10 max-w-7xl mx-auto pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] sm:px-4 md:px-6 py-5 sm:py-6 md:py-8 pb-24 sm:pb-6 md:pb-8 space-y-5 sm:space-y-8">
                {/* Module Sections */}
                {studyPlan.modules.map((module, index) => {
                    const moduleLocked = isModuleLocked(module.moduleId);
                    const hasFreePreviewLesson = module.lessons?.some((lesson, lessonIndex) =>
                        isLessonFreePreview(lesson, module, lessonIndex)
                    );
                    return (
                    <div
                        key={module.moduleId}
                      className={`relative rounded-xl sm:rounded-2xl md:rounded-3xl border-2 sm:border-[3px] p-3 sm:p-6 md:p-8 overflow-hidden group ${moduleLocked ? 'border-black bg-gradient-to-br from-white via-gray-50 to-gray-100' : 'border-black bg-white'}`}
                        style={{animationDelay: `${index * 100}ms`}}
                    >
                        {/* Corner decorations, smaller on mobile */}
                        <div
                            className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 border-t-4 border-l-4 border-black/10 rounded-tl-xl sm:rounded-tl-3xl"/>
                        <div
                            className="absolute bottom-0 right-0 w-8 h-8 sm:w-12 sm:h-12 border-b-4 border-r-4 border-black/10 rounded-br-xl sm:rounded-br-3xl"/>

                        {/* Shine effect */}
                        <div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>

                        <div className="relative z-10">
                            {/* Module Header, mobile-optimized type scale */}
                            <div className="mb-4 sm:mb-6">
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1.5 sm:mb-2">
                                    <h2 className="font-space text-xl sm:text-2xl md:text-3xl font-black text-black tracking-tight leading-tight">
                                        {module.title}
                                    </h2>
                                {moduleLocked && !hasFreePreviewLesson && (
                                  <span className="inline-flex items-center rounded-full border border-black/20 bg-black px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
                                    {t('premium.only')}
                                  </span>
                                )}
                                </div>
                                <p className="font-sans text-sm sm:text-base text-black/60 leading-snug">
                                    {module.description}
                                </p>
                            </div>

                           {/* Lessons */}
<div className="space-y-3 sm:space-y-4">
  {module.lessons.map((lesson, lessonIndex) => {
    const isCompleted = lessonStatusMap[lesson.lessonId] === 'completed';
    const isComingSoon = lessonStatusMap[lesson.lessonId] === 'coming_soon';
    const isLocked = moduleLocked && !isLessonFreePreview(lesson, module, lessonIndex);
    const isLessonHovered = hoveredLessonId === lesson.lessonId && !isCompleted && !isLocked;
    const hasVideo = !!lesson.videoId;
    const isDualBox = hasVideo && lesson.type !== 'video';
    const openVideo = (e) => {
      e?.stopPropagation();
      setVideoModal({ videoId: lesson.videoId, title: lesson.title });
    };

    // Shared inner card content (wave + icon + badges + text + arrow)
    const cardInner = (hovered) => (
      <>
        {!isCompleted && !isLocked && (
          <div className="hidden sm:block" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: hovered ? '100%' : '0%', background: 'linear-gradient(to right, #d1d5db, #9ca3af, #6b7280, #000000)', transition: 'width 0.8s ease-out', zIndex: 0 }} />
        )}
        <div className="relative z-10 flex items-center gap-2.5 sm:gap-6">
          <div className={`w-10 h-10 sm:w-16 sm:h-20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${hovered ? 'sm:scale-110 sm:rotate-6' : ''}`} style={{ backgroundColor: isCompleted ? '#ffffff' : isLocked ? '#111827' : (hovered ? '#111111' : '#000000') }}>
            {isCompleted ? (<CheckCircle className="w-6 h-6 sm:w-10 sm:h-10 text-black" />) : lesson.type === 'video' ? (
              <svg viewBox="0 0 40 40" className="w-6 h-6 sm:w-10 sm:h-10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <style>{`@keyframes teacherArm{0%,100%{transform:rotate(-15deg)}40%{transform:rotate(20deg)}70%{transform:rotate(-5deg)}}@keyframes boardWrite{0%{stroke-dashoffset:30;opacity:0}20%{opacity:1}60%{stroke-dashoffset:0;opacity:1}85%,100%{stroke-dashoffset:0;opacity:.3}}@keyframes boardLine2{0%,30%{stroke-dashoffset:20;opacity:0}55%{opacity:1}80%{stroke-dashoffset:0;opacity:1}95%,100%{stroke-dashoffset:0;opacity:.3}}.teacher-arm{animation:teacherArm 2.4s ease-in-out infinite;transform-origin:22px 22px;transform-box:fill-box}.board-text-1{stroke-dasharray:30;animation:boardWrite 2.4s ease-in-out infinite}.board-text-2{stroke-dasharray:20;animation:boardLine2 2.4s ease-in-out infinite}`}</style>
                <rect x="1" y="3" width="24" height="16" rx="1.5" fill="white" opacity="0.15" stroke="white" strokeWidth="1.2"/>
                <path className="board-text-1" d="M4 9 Q10 7 16 9 Q20 10 22 9" stroke="white" strokeWidth="1.1" strokeLinecap="round" fill="none"/>
                <path className="board-text-2" d="M4 13 Q9 11.5 14 13 Q17 13.5 20 13" stroke="white" strokeWidth="1.1" strokeLinecap="round" fill="none"/>
                <line x1="13" y1="19" x2="10" y2="25" stroke="white" strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/>
                <line x1="13" y1="19" x2="16" y2="25" stroke="white" strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/>
                <circle cx="32" cy="9" r="3.5" fill="white" opacity="0.9"/>
                <path d="M28 14 Q32 12.5 36 14 L36 23 Q32 24.5 28 23 Z" fill="white" opacity="0.75"/>
                <g className="teacher-arm"><line x1="28" y1="17" x2="22" y2="14" stroke="white" strokeWidth="1.8" strokeLinecap="round" opacity="0.9"/><circle cx="22" cy="14" r="1" fill="white" opacity="0.9"/></g>
                <line x1="30" y1="23" x2="29" y2="30" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
                <line x1="34" y1="23" x2="35" y2="30" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-6 h-6 sm:w-10 sm:h-10 text-white flex-shrink-0" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <style>{`@keyframes pageTurn{0%{transform:rotateY(0deg) skewY(0deg);opacity:1}40%{transform:rotateY(-70deg) skewY(-4deg);opacity:.7}60%{transform:rotateY(-70deg) skewY(-4deg);opacity:.7}100%{transform:rotateY(0deg) skewY(0deg);opacity:1}}@keyframes pageShimmer{0%,100%{opacity:0}45%,55%{opacity:.35}}.book-page{animation:pageTurn 2.2s ease-in-out infinite;transform-origin:left center;transform-box:fill-box}.page-shine{animation:pageShimmer 2.2s ease-in-out infinite}`}</style>
                <rect x="2" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.18"/><rect x="12" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.18"/>
                <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="1.2" opacity="0.5"/>
                <line x1="4" y1="9" x2="10" y2="9" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="4" y1="11.5" x2="10" y2="11.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="4" y1="14" x2="10" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                <line x1="14" y1="9" x2="20" y2="9" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="14" y1="11.5" x2="20" y2="11.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="14" y1="14" x2="20" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                <g className="book-page"><rect x="12" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.55"/><line x1="14" y1="9" x2="20" y2="9" stroke="white" strokeWidth="0.9" opacity="0.6"/><line x1="14" y1="11.5" x2="20" y2="11.5" stroke="white" strokeWidth="0.9" opacity="0.6"/><line x1="14" y1="14" x2="20" y2="14" stroke="white" strokeWidth="0.9" opacity="0.6"/><rect className="page-shine" x="12" y="5" width="10" height="14" rx="1" fill="white"/></g>
              </svg>
            )}
          </div>
          {isCompleted && (
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white text-black text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center gap-1 border-2 border-black">
              <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />{t('studyPlan.completed', { defaultValue: 'Completed' })}
            </div>
          )}
          {isComingSoon && (
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-black text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full">{t('message.comingSoon')}</div>
          )}
          {isLocked && !isComingSoon && !isCompleted && (
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-gray-100 text-gray-700 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border border-gray-300">{t('premium.only')}</div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className={`font-space font-black text-base sm:text-xl md:text-2xl mb-1 sm:mb-2 leading-tight transition-colors duration-500 ${hovered ? 'sm:text-white' : ''}`} style={{ color: isCompleted ? '#ffffff' : (hovered ? '#ffffff' : '#000000') }}>{lesson.title}</h4>
            <p className={`font-sans text-sm mb-1 sm:mb-2 transition-colors duration-500 line-clamp-2 leading-snug ${hovered ? 'sm:text-white/80' : ''}`} style={{ color: isCompleted ? 'rgba(255,255,255,0.8)' : (hovered ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)') }}>{lesson.description}</p>
            <p className={`font-sans text-xs flex items-center gap-1.5 sm:gap-2 transition-colors duration-500 ${hovered ? 'sm:text-white/60' : ''}`} style={{ color: isCompleted ? 'rgba(255,255,255,0.6)' : (hovered ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)') }}>
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />{lesson.duration} min • {lesson.xp} XP
            </p>
          </div>
          <ChevronRight className={`w-5 h-5 sm:w-8 sm:h-8 transition-all duration-500 flex-shrink-0 ${hovered ? 'sm:translate-x-2' : ''}`} style={{ color: isCompleted ? '#ffffff' : (hovered ? '#ffffff' : 'rgba(0,0,0,0.4)') }} />
        </div>
        {!isCompleted && (
          <div className="hidden sm:block absolute top-3 right-3 w-2 h-2 rounded-full bg-black transition-all duration-300" style={{ opacity: hovered ? 1 : 0, transform: hovered ? 'scale(1)' : 'scale(0)' }} />
        )}
      </>
    );

    if (isDualBox) {
      return (
        <div
          key={lesson.lessonId}
          data-lesson-id={lesson.lessonId}
          className={`group/task relative rounded-xl sm:rounded-2xl border-2 p-3 sm:p-5 lg:p-6 overflow-hidden transition-all duration-300 ${
            isCompleted ? 'bg-black border-black'
            : isLocked ? 'bg-gradient-to-br from-white via-gray-50 to-gray-100 border-black'
            : 'bg-white border-black'
          }`}
          style={{ animationDelay: `${lessonIndex * 50}ms` }}
        >
          {/* Lesson title row */}
          <div className="mb-3 sm:mb-4">
            <h4 className={`font-space font-black text-base sm:text-xl md:text-2xl leading-tight ${isCompleted ? 'text-white' : 'text-black'}`}>{lesson.title}</h4>
            {lesson.description && (
              <p className={`font-sans text-sm mt-1 line-clamp-2 leading-snug ${isCompleted ? 'text-white/70' : 'text-black/60'}`}>{lesson.description}</p>
            )}
            <p className={`font-sans text-xs flex items-center gap-1.5 mt-1.5 ${isCompleted ? 'text-white/50' : 'text-black/40'}`}>
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />{lesson.duration} min • {lesson.xp} XP
            </p>
          </div>
          {/* Two option cards side by side */}
          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3">
            {/* Video card. LEFT */}
            <div
              onClick={() => { if (isLocked) { setShowPremiumOverlay(true); return; } openVideo(); }}
              className={`flex-1 flex items-center gap-3 sm:gap-4 rounded-lg sm:rounded-xl border p-3 sm:p-4 transition-all duration-200 active:scale-[0.98] cursor-pointer ${
                isCompleted ? 'border-white/20 bg-white/5 sm:hover:bg-white/10' : 'border-black/15 bg-gray-50 sm:hover:bg-gray-100 sm:hover:shadow-md'
              }`}
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-white/15' : 'bg-black'}`}>
                <svg viewBox="0 0 40 40" className="w-5 h-5 sm:w-6 sm:h-6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <style>{`@keyframes ta{0%,100%{transform:rotate(-15deg)}40%{transform:rotate(20deg)}70%{transform:rotate(-5deg)}}@keyframes bw{0%{stroke-dashoffset:30;opacity:0}20%{opacity:1}60%{stroke-dashoffset:0;opacity:1}85%,100%{stroke-dashoffset:0;opacity:.3}}@keyframes bl{0%,30%{stroke-dashoffset:20;opacity:0}55%{opacity:1}80%{stroke-dashoffset:0;opacity:1}95%,100%{stroke-dashoffset:0;opacity:.3}}.ta{animation:ta 2.4s ease-in-out infinite;transform-origin:22px 22px;transform-box:fill-box}.bt1{stroke-dasharray:30;animation:bw 2.4s ease-in-out infinite}.bt2{stroke-dasharray:20;animation:bl 2.4s ease-in-out infinite}`}</style>
                  <rect x="1" y="3" width="24" height="16" rx="1.5" fill="white" opacity="0.15" stroke="white" strokeWidth="1.2"/>
                  <path className="bt1" d="M4 9 Q10 7 16 9 Q20 10 22 9" stroke="white" strokeWidth="1.1" strokeLinecap="round" fill="none"/>
                  <path className="bt2" d="M4 13 Q9 11.5 14 13 Q17 13.5 20 13" stroke="white" strokeWidth="1.1" strokeLinecap="round" fill="none"/>
                  <line x1="13" y1="19" x2="10" y2="25" stroke="white" strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/>
                  <line x1="13" y1="19" x2="16" y2="25" stroke="white" strokeWidth="1.2" strokeLinecap="round" opacity="0.7"/>
                  <circle cx="32" cy="9" r="3.5" fill="white" opacity="0.9"/>
                  <path d="M28 14 Q32 12.5 36 14 L36 23 Q32 24.5 28 23 Z" fill="white" opacity="0.75"/>
                  <g className="ta"><line x1="28" y1="17" x2="22" y2="14" stroke="white" strokeWidth="1.8" strokeLinecap="round" opacity="0.9"/><circle cx="22" cy="14" r="1" fill="white" opacity="0.9"/></g>
                  <line x1="30" y1="23" x2="29" y2="30" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
                  <line x1="34" y1="23" x2="35" y2="30" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-space font-bold text-sm sm:text-base leading-tight ${isCompleted ? 'text-white' : 'text-black'}`}>Video-Lektion</div>
                <div className={`text-xs mt-0.5 ${isCompleted ? 'text-white/50' : 'text-black/40'}`}>{t('studyPlan.videoLesson', { defaultValue: 'Video Lesson' })}</div>
              </div>
              <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${isCompleted ? 'text-white/40' : 'text-black/30'}`} />
            </div>
            {/* Text card. RIGHT */}
            <div
              onClick={() => { if (isLocked) { setShowPremiumOverlay(true); return; } handleStartLesson(lesson, module, lessonIndex); }}
              className={`flex-1 flex items-center gap-3 sm:gap-4 rounded-lg sm:rounded-xl border p-3 sm:p-4 transition-all duration-200 active:scale-[0.98] cursor-pointer ${
                isCompleted ? 'border-white/20 bg-white/5 sm:hover:bg-white/10' : 'border-black/15 bg-gray-50 sm:hover:bg-gray-100 sm:hover:shadow-md'
              }`}
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-white/15' : 'bg-black'}`}>
                <svg viewBox="0 0 24 24" className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <style>{`@keyframes pt{0%{transform:rotateY(0) skewY(0);opacity:1}40%{transform:rotateY(-70deg) skewY(-4deg);opacity:.7}60%{transform:rotateY(-70deg) skewY(-4deg);opacity:.7}100%{transform:rotateY(0) skewY(0);opacity:1}}@keyframes ps{0%,100%{opacity:0}45%,55%{opacity:.35}}.bp{animation:pt 2.2s ease-in-out infinite;transform-origin:left center;transform-box:fill-box}.psh{animation:ps 2.2s ease-in-out infinite}`}</style>
                  <rect x="2" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.18"/><rect x="12" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.18"/>
                  <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="1.2" opacity="0.5"/>
                  <line x1="4" y1="9" x2="10" y2="9" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="4" y1="11.5" x2="10" y2="11.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="4" y1="14" x2="10" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                  <line x1="14" y1="9" x2="20" y2="9" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="14" y1="11.5" x2="20" y2="11.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/><line x1="14" y1="14" x2="20" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                  <g className="bp"><rect x="12" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.55"/><line x1="14" y1="9" x2="20" y2="9" stroke="white" strokeWidth="0.9" opacity="0.6"/><line x1="14" y1="11.5" x2="20" y2="11.5" stroke="white" strokeWidth="0.9" opacity="0.6"/><line x1="14" y1="14" x2="20" y2="14" stroke="white" strokeWidth="0.9" opacity="0.6"/><rect className="psh" x="12" y="5" width="10" height="14" rx="1" fill="white"/></g>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-space font-bold text-sm sm:text-base leading-tight ${isCompleted ? 'text-white' : 'text-black'}`}>Text-Lektion</div>
                <div className={`text-xs mt-0.5 ${isCompleted ? 'text-white/50' : 'text-black/40'}`}>{t('studyPlan.textLesson', { defaultValue: 'Text Lesson' })}</div>
              </div>
              <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${isCompleted ? 'text-white/40' : 'text-black/30'}`} />
            </div>
          </div>
          {/* Badges */}
          {isCompleted && (
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white text-black text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full flex items-center gap-1 border-2 border-black">
              <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />{t('studyPlan.completed', { defaultValue: 'Completed' })}
            </div>
          )}
          {isLocked && !isCompleted && (
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-gray-100 text-gray-700 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border border-gray-300">{t('premium.only')}</div>
          )}
        </div>
      );
    }

    return (
      <div
        key={lesson.lessonId}
        data-lesson-id={lesson.lessonId}
        onClick={() => {
          if (isComingSoon) return;
          if (isLocked) {
            setShowPremiumOverlay(true);
            return;
          }
          handleStartLesson(lesson, module, lessonIndex);
        }}
        onMouseEnter={() => !isCompleted && !isLocked && !isComingSoon && setHoveredLessonId(lesson.lessonId)}
        onMouseLeave={() => setHoveredLessonId(null)}
        className={`group/task relative rounded-xl sm:rounded-2xl border-2 p-3 sm:p-6 lg:p-8 overflow-hidden transition-all duration-300 active:scale-[0.98] active:bg-gray-50 ${
          isCompleted ? 'bg-black border-black cursor-default'
          : isComingSoon ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-70'
          : isLocked ? 'bg-gradient-to-br from-white via-gray-50 to-gray-100 border-black sm:hover:shadow-[0_16px_32px_rgba(17,24,39,0.14)] cursor-pointer'
          : 'bg-white border-black sm:hover:shadow-2xl cursor-pointer'
        }`}
        style={{ animationDelay: `${lessonIndex * 50}ms` }}
      >
        {cardInner(isLessonHovered)}
      </div>
    );
  })}
</div>

                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Bottom Navigation. Home, Exam Simulations, Practice */}
            <GrammarBottomNav
                buttons={[
                    {
                        id: 'home',
                        icon: Home,
                        label: t('nav.home', { defaultValue: 'Home' }),
                        onClick: () => navigate('/dashboard'),
                      color: '#111827',
                    },
                    {
                        id: 'exam',
                        icon: ClipboardList,
                        label: t('nav.examSimulations', { defaultValue: 'Exam Simulations' }),
                        onClick: () => navigate('/exam'),
                      color: '#111827',
                    },
                    {
                        id: 'practice',
                        icon: BookOpen,
                        label: t('nav.practice', { defaultValue: 'Practice' }),
                        onClick: () => navigate('/practice'),
                      color: '#111827',
                    },
                ]}
            />

            {/* Keyframes */}
            <style jsx>{`
                @keyframes float {
                    0%, 100% {
                        transform: translateY(0) rotate(0deg);
                    }
                    50% {
                        transform: translateY(-10px) rotate(5deg);
                    }
                }

                .animate-float {
                    animation: float 6s ease-in-out infinite;
                }

                /* Only apply hover transforms on devices that support hover */
                @media (hover: none) {
                    .group\\/task > div[style*="transform"] {
                        transform: scale(1) !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default StudyPlanPage;